import { ComponentProps } from 'react';

import * as AccordionPrimitive from '@radix-ui/react-accordion';

import { ChevronDownIcon } from '@/assets/icons';
import { cn } from '@/utils/tailwind';

type AccordionProps = ComponentProps<typeof AccordionPrimitive.Root>;
type AccordionItemProps = ComponentProps<typeof AccordionPrimitive.Item>;
type AccordionTriggerProps = ComponentProps<typeof AccordionPrimitive.Trigger>;
type AccordionContentProps = ComponentProps<typeof AccordionPrimitive.Content>;

const Accordion = ({ ...props }: AccordionProps) => {
  return <AccordionPrimitive.Root data-slot="accordion" {...props} />;
};

const AccordionItem = ({ ...props }: AccordionItemProps) => {
  return <AccordionPrimitive.Item data-slot="accordion-item" {...props} />;
};

const AccordionTrigger = ({ className, children, ...props }: AccordionTriggerProps) => {
  return (
    <AccordionPrimitive.Header className="flex">
      <AccordionPrimitive.Trigger
        data-slot="accordion-trigger"
        className={cn(
          'cursor-pointer flex flex-1 items-center justify-between gap-4 rounded-md text-left transition-all outline-none disabled:pointer-events-none disabled:opacity-50 [&[data-state=open]>svg]:rotate-180',
          className
        )}
        {...props}
      >
        {children}
        <ChevronDownIcon className="text-custom-warm-grey-500 pointer-events-none size-5 shrink-0 transition-transform duration-200" />
      </AccordionPrimitive.Trigger>
    </AccordionPrimitive.Header>
  );
};

const AccordionContent = ({ className, children, ...props }: AccordionContentProps) => {
  return (
    <AccordionPrimitive.Content
      data-slot="accordion-content"
      className="data-[state=closed]:animate-accordion-up data-[state=open]:animate-accordion-down overflow-hidden"
      {...props}
    >
      <div className={cn('p-0', className)}>{children}</div>
    </AccordionPrimitive.Content>
  );
};

const AccordionTableTriggerRow = ({
  value,
  children,
}: {
  value: string;
  children: React.ReactNode;
}) => {
  return (
    <AccordionTrigger value={value}>
      <tr className="cursor-pointer bg-custom-warm-grey-50">
        <td
          colSpan={2}
          className="px-5 py-[14px] text-left text-body-14-regular !text-custom-warm-grey-800"
        >
          {children}
        </td>
      </tr>
    </AccordionTrigger>
  );
};

export { Accordion, AccordionContent, AccordionItem, AccordionTableTriggerRow, AccordionTrigger };
